import { useEffect, useState, useCallback, useMemo } from 'react';
import StatCard from '../components/common/StatCard';
import Spinner from '../components/common/Spinner';
import EmptyState from '../components/common/EmptyState';
import { api } from '../services/apiClient';
import { fmt } from '../utils/formatters';

const thStyle = {
  textAlign: 'left',
  padding: '10px 14px',
  fontSize: 12.5,
  fontWeight: 600,
  color: 'var(--muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  borderBottom: '1px solid var(--border)',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '10px 14px',
  fontSize: 13.5,
  borderBottom: '1px solid var(--border)',
  whiteSpace: 'nowrap',
};

export default function LedgerPage({ type, toast }) {
  const isSale = type === 'sale';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(null);

  // Deletion state
  const [deletingRow, setDeletingRow] = useState(null);
  const [showDateModal, setShowDateModal] = useState(false);
  const [selectedDeleteDate, setSelectedDeleteDate] = useState('');
  const [deletingDate, setDeletingDate] = useState(false);

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const result = isSale ? await api.salesLedger(m) : await api.purchaseLedger(m);
      setData(result);
      if (result.month) setMonth(result.month);
    } catch (e) {
      toast?.('Failed to load ledger: ' + e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [isSale, toast]);

  useEffect(() => { load(month); /* eslint-disable-next-line */ }, [type]);

  function handleMonthChange(e) {
    const m = e.target.value;
    setMonth(m);
    load(m);
  }

  const rows = data?.rows || [];
  const uniqueDates = useMemo(() => {
    return [...new Set(rows.map(r => r.date).filter(Boolean))];
  }, [rows]);

  async function handleDeleteRow(r) {
    const itemLabel = isSale ? (r.invoiceNo || 'Sale') : (r.companyName || 'Purchase');
    const amountLabel = fmt(isSale ? r.totalAmount : r.amountPaid);
    if (!window.confirm(`Are you sure you want to delete the ledger entry for ${r.date} (${itemLabel}) - ${amountLabel}?\n\nThis will remove the entry from the Ledger Excel file on Google Drive and delete the invoice record from the database.`)) {
      return;
    }

    setDeletingRow(r.row);
    try {
      await api.deleteLedgerEntry(type, {
        month,
        row: r.row,
        invoiceNo: r.invoiceNo,
        date: r.date,
      });
      toast?.('Ledger entry deleted from Drive and Database', 'success');
      await load(month);
    } catch (e) {
      toast?.('Failed to delete ledger entry: ' + e.message, 'error');
    } finally {
      setDeletingRow(null);
    }
  }

  async function handleDeleteByDate() {
    if (!selectedDeleteDate) return;
    const count = rows.filter(r => r.date === selectedDeleteDate).length;
    const msg = count > 0
      ? `Are you sure you want to delete all ${count} ledger entries for date ${selectedDeleteDate}?\n\nThis will delete the entries from the Ledger Excel file on Google Drive and delete the invoice records from the database.`
      : `Are you sure you want to delete all ledger entries and invoices for date ${selectedDeleteDate}?`;

    if (!window.confirm(msg)) return;

    setDeletingDate(true);
    try {
      await api.deleteLedgerByDate(type, selectedDeleteDate);
      toast?.(`All entries for ${selectedDeleteDate} deleted from Drive and Database`, 'success');
      setShowDateModal(false);
      await load(month);
    } catch (e) {
      toast?.('Failed to delete entries by date: ' + e.message, 'error');
    } finally {
      setDeletingDate(false);
    }
  }

  async function handleDeleteLedger() {
    if (!window.confirm(`Are you sure you want to delete the entire ${isSale ? 'Sales' : 'Purchase'} Ledger Excel file from Google Drive? This action cannot be undone.`)) return;
    try {
      await api.deleteLedger(type);
      toast?.('Ledger file deleted successfully', 'success');
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (e) {
      toast?.('Failed to delete ledger file: ' + e.message, 'error');
    }
  }

  if (loading && !data) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spinner dark /> <span style={{ marginLeft: 10, color: 'var(--muted)' }}>Loading ledger…</span>
      </div>
    );
  }

  if (!data?.configured) {
    return (
      <EmptyState
        icon="📒"
        title={`${isSale ? 'Sales' : 'Purchase'} Ledger folder not set`}
        sub={`Set the ${isSale ? 'Sales' : 'Purchase'} Ledger folder ID and file name in Settings to start tracking here.`}
      />
    );
  }

  if (!data.exists) {
    return (
      <EmptyState
        icon="📒"
        title={`No ${isSale ? 'sales' : 'purchase'} ledger file yet`}
        sub="It will be created automatically the first time a matching invoice is calculated."
      />
    );
  }

  const totals = data.totals;

  return (
    <div>
      <div className="stat-grid no-print" style={{ marginBottom: 20 }}>
        <StatCard label="Net Amount" value={fmt(totals.netAmount)} variant={isSale ? 'gold' : 'blue'} />
        <StatCard label="VAT" value={fmt(totals.vatAmount)} variant={isSale ? 'gold' : 'blue'} />
        <StatCard
          label={isSale ? 'Total Amount' : 'Amount Paid'}
          value={fmt(isSale ? totals.totalAmount : totals.amountPaid)}
          variant={isSale ? 'gold' : 'blue'}
        />
        <StatCard label="Cash / Card" value={`${fmt(totals.cash)} / ${fmt(totals.card)}`} variant={isSale ? 'gold' : 'blue'} />
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 12, flexWrap: 'wrap', gap: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {data.months?.length > 0 && (
            <select
              className="form-input"
              value={month || ''}
              onChange={handleMonthChange}
              style={{ width: 'auto', minWidth: 140 }}
            >
              {data.months.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => load(month)} style={{ cursor: 'pointer' }}>
            ⟳ Refresh
          </button>
        </div>
        {data.webViewLink && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              className="btn btn-outline btn-sm"
              style={{
                borderColor: 'var(--border)',
                color: '#b91c1c',
                background: 'transparent',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
              onClick={() => {
                setSelectedDeleteDate(uniqueDates[0] || '');
                setShowDateModal(true);
              }}
            >
              📅 Delete by Date
            </button>
            <a
              href={data.webViewLink}
              target="_blank"
              rel="noreferrer"
              className="btn btn-outline btn-sm"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              👁 View Ledger File
            </a>
            <button
              className="btn btn-outline btn-sm"
              style={{
                borderColor: 'var(--danger, #9b2335)',
                color: 'var(--danger, #9b2335)',
                background: 'transparent',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
              onClick={handleDeleteLedger}
            >
              🗑 Delete Entire Ledger File
            </button>
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <EmptyState icon="📒" title="No rows yet" sub="Scan Drive or upload receipts to populate this ledger." />
      ) : (
        <div style={{ overflowX: 'auto', background: 'var(--surface)', borderRadius: 'var(--r-lg)', boxShadow: 'var(--shadow-sm)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                {isSale ? (
                  <>
                    <th style={thStyle}>Invoice No.</th>
                    <th style={thStyle}>Sales Channel</th>
                  </>
                ) : (
                  <th style={thStyle}>Company Name</th>
                )}
                <th style={thStyle}>Payment Method</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Net Amount</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>VAT</th>
                {isSale && <th style={{ ...thStyle, textAlign: 'right' }}>Discount</th>}
                <th style={{ ...thStyle, textAlign: 'right' }}>{isSale ? 'Total' : 'Amount Paid'}</th>
                <th style={{ ...thStyle, textAlign: 'center', width: 90 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice().reverse().map(r => (
                <tr key={r.row}>
                  <td style={tdStyle}>{r.date}</td>
                  {isSale ? (
                    <>
                      <td style={tdStyle}>{r.invoiceNo}</td>
                      <td style={tdStyle}>{r.salesChannel}</td>
                    </>
                  ) : (
                    <td style={tdStyle}>{r.companyName}</td>
                  )}
                  <td style={tdStyle}>{r.paymentMethod}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(r.netAmount)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(r.vatAmount)}</td>
                  {isSale && <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(r.discount)}</td>}
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                    {fmt(isSale ? r.totalAmount : r.amountPaid)}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button
                      className="btn btn-outline btn-sm"
                      style={{
                        borderColor: '#fca5a5',
                        color: '#dc2626',
                        padding: '3px 8px',
                        fontSize: 12,
                        cursor: deletingRow === r.row ? 'not-allowed' : 'pointer',
                        borderRadius: 4,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                      title="Delete this ledger row from Drive & Database"
                      disabled={deletingRow === r.row}
                      onClick={() => handleDeleteRow(r)}
                    >
                      {deletingRow === r.row ? <Spinner size={12} dark /> : '🗑 Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete by Date Modal */}
      {showDateModal && (
        <div className="modal-backdrop" onClick={() => setShowDateModal(false)}>
          <div className="modal-box" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <span className="modal-title">📅 Delete Ledger by Date</span>
              <button
                style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--muted)' }}
                onClick={() => setShowDateModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <p style={{ margin: 0, fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.5 }}>
                Select a date to delete all entries for that day from both the <strong>Google Drive Ledger Excel file</strong> and the <strong>database</strong> (including invoice records & files).
              </p>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                  Select Date:
                </label>
                {uniqueDates.length > 0 ? (
                  <select
                    className="form-input"
                    value={selectedDeleteDate}
                    onChange={e => setSelectedDeleteDate(e.target.value)}
                    style={{ width: '100%' }}
                  >
                    {uniqueDates.map(d => {
                      const count = rows.filter(r => r.date === d).length;
                      return <option key={d} value={d}>{d} ({count} {count === 1 ? 'entry' : 'entries'})</option>;
                    })}
                  </select>
                ) : (
                  <input
                    type="date"
                    className="form-input"
                    value={selectedDeleteDate}
                    onChange={e => setSelectedDeleteDate(e.target.value)}
                    style={{ width: '100%' }}
                  />
                )}
              </div>

              <div style={{
                padding: 12,
                borderRadius: 'var(--r-md, 6px)',
                background: '#fef2f2',
                border: '1px solid #fee2e2',
                color: '#991b1b',
                fontSize: 12.5,
                lineHeight: 1.4,
              }}>
                ⚠️ <strong>Warning:</strong> This will delete all {isSale ? 'sales' : 'purchase'} invoices for this date from the database and remove their rows from the Ledger Excel workbook.
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn btn-ghost" onClick={() => setShowDateModal(false)} disabled={deletingDate}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                style={{ background: '#dc2626', borderColor: '#dc2626', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                onClick={handleDeleteByDate}
                disabled={!selectedDeleteDate || deletingDate}
              >
                {deletingDate ? <Spinner size={14} /> : '🗑 Delete All for This Date'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
